import { ECONOMIC_CALENDAR_STORAGE_KEYS } from '@/lib/localStorageKeys/economic-calendar';
import { EconomicEvent, EconomicCalendarFilters } from './types';
import { DEFAULT_ECONOMIC_CALENDAR_FILTERS } from './default-data';

export const getStoredEconomicCalendar = (): EconomicEvent[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const storedData = localStorage.getItem(ECONOMIC_CALENDAR_STORAGE_KEYS.CORE);
    
    if (!storedData) {
      return [];
    }

    const parsedData = JSON.parse(storedData);
    
    if (!Array.isArray(parsedData)) {
      console.error('Invalid economic calendar data in localStorage');
      return [];
    }

    return parsedData;
  } catch (error) {
    console.error('Error loading economic calendar from localStorage:', error);
    return [];
  }
};

export const saveEconomicCalendar = (events: EconomicEvent[]): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(ECONOMIC_CALENDAR_STORAGE_KEYS.CORE, JSON.stringify(events));
  } catch (error) {
    console.error('Error saving economic calendar to localStorage:', error);
  }
};

export const getStoredEconomicCalendarFilters = (): EconomicCalendarFilters => {
  if (typeof window === 'undefined') {
    return DEFAULT_ECONOMIC_CALENDAR_FILTERS;
  }

  try {
    const storedData = localStorage.getItem(ECONOMIC_CALENDAR_STORAGE_KEYS.FILTERS);
    
    if (!storedData) {
      // Save defaults and return them
      saveEconomicCalendarFilters(DEFAULT_ECONOMIC_CALENDAR_FILTERS);
      return DEFAULT_ECONOMIC_CALENDAR_FILTERS;
    }

    const parsedData = JSON.parse(storedData);
    
    // Merge with defaults to ensure all fields exist
    return {
      ...DEFAULT_ECONOMIC_CALENDAR_FILTERS,
      ...parsedData,
    };
  } catch (error) {
    console.error('Error loading economic calendar filters from localStorage:', error);
    return DEFAULT_ECONOMIC_CALENDAR_FILTERS;
  }
};

export const saveEconomicCalendarFilters = (filters: EconomicCalendarFilters): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(ECONOMIC_CALENDAR_STORAGE_KEYS.FILTERS, JSON.stringify(filters));
  } catch (error) {
    console.error('Error saving economic calendar filters to localStorage:', error);
  }
};

/**
 * Get the size of stored economic calendar events in bytes
 * @returns Size in bytes or 0 if not found
 */
export function getStoredEconomicCalendarEventsValue(): number {
  if (typeof window === 'undefined') return 0;
  
  try {
    const stored = localStorage.getItem(ECONOMIC_CALENDAR_STORAGE_KEYS.CORE);
    
    if (!stored) return 0;
    
    // Calculate size in bytes (JavaScript strings are UTF-16, so each character is 2 bytes)
    return stored.length * 2;
  } catch (error) {
    console.error('Error getting economic calendar events size:', error);
    return 0;
  }
}

/**
 * Get the size of stored economic calendar filters in bytes
 * @returns Size in bytes or 0 if not found
 */
export function getStoredEconomicCalendarFiltersValue(): number {
  if (typeof window === 'undefined') return 0;
  
  try {
    const stored = localStorage.getItem(ECONOMIC_CALENDAR_STORAGE_KEYS.FILTERS);
    
    if (!stored) return 0;
    
    // Calculate size in bytes (JavaScript strings are UTF-16, so each character is 2 bytes)
    return stored.length * 2;
  } catch (error) {
    console.error('Error getting economic calendar filters size:', error);
    return 0;
  }
}

/**
 * Get the total size of economic calendar storage in bytes
 * @returns Size in bytes or 0 if not found
 */
export function getStoredEconomicCalendarSyncValue(): number {
  if (typeof window === "undefined") return 0;
  try {
    const stored = localStorage.getItem(ECONOMIC_CALENDAR_STORAGE_KEYS.SYNC);
    if (!stored) return 0;
    return stored.length * 2;
  } catch {
    return 0;
  }
}

export function getStoredEconomicCalendarValue(): number {
  return (
    getStoredEconomicCalendarEventsValue() +
    getStoredEconomicCalendarFiltersValue() +
    getStoredEconomicCalendarSyncValue()
  );
}

/**
 * Clear stored economic calendar events
 * @returns Success status
 */
export function clearStoredEconomicCalendarEvents(): boolean {
  if (typeof window === 'undefined') return false;
  
  try {
    localStorage.removeItem(ECONOMIC_CALENDAR_STORAGE_KEYS.CORE);
    return true;
  } catch (error) {
    console.error('Error clearing economic calendar events:', error);
    return false;
  }
}

/**
 * Clear stored economic calendar filters
 * @returns Success status
 */
export function clearStoredEconomicCalendarFilters(): boolean {
  if (typeof window === 'undefined') return false;
  
  try {
    localStorage.removeItem(ECONOMIC_CALENDAR_STORAGE_KEYS.FILTERS);
    return true;
  } catch (error) {
    console.error('Error clearing economic calendar filters:', error);
    return false;
  }
}

/**
 * Clear all economic calendar data
 * @returns Success status
 */
export function clearStoredEconomicCalendar(): boolean {
  const eventsCleared = clearStoredEconomicCalendarEvents();
  const filtersCleared = clearStoredEconomicCalendarFilters();
  let syncCleared = true;
  if (typeof window !== "undefined") {
    try {
      localStorage.removeItem(ECONOMIC_CALENDAR_STORAGE_KEYS.SYNC);
    } catch {
      syncCleared = false;
    }
  }
  return eventsCleared && filtersCleared && syncCleared;
}